"use client";
import { useCallback, useState } from "react";
import { Trash2 } from "lucide-react";
import { useTransactionContext } from "./TransactionContext";
import { CategorySelect } from "./CategorySelect";
import type { TransactionWithCategory, Currency } from "@/types";
import { formatCurrency } from "@/components/cashflow/CashflowContext";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { cfFetch } from "../../api/apiClient";
import { formatAppDate } from "../../utils/date";
import { isUncategorizedTransaction } from "./uncategorized";

function formatSignedAmount(amount: number, currency: Currency): string {
  const core = formatCurrency(Math.abs(amount), currency);
  return amount >= 0 ? `+${core}` : `-${core}`;
}

function formatBalance(balance: number | null, currency: Currency): string {
  if (balance == null) return "—";
  return formatCurrency(balance, currency);
}

export function TransactionTable() {
  const {
    transactions,
    total,
    totalPages,
    page,
    isLoading,
    updateFilter,
    mutate,
  } = useTransactionContext();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    { type: "single"; id: string } | { type: "bulk"; ids: string[] } | null
  >(null);

  const allIds = transactions.map((tx: TransactionWithCategory) => tx.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => new Set(Array.from(prev).concat(allIds)));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUpdateCategory = useCallback(
    async (id: string, categoryId: string | null) => {
      const res = await cfFetch(`/transactions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryId }),
      });
      if (!res.ok) throw new Error("Failed to update");
      mutate();
    },
    [mutate],
  );

  const requestDeleteSingle = useCallback((id: string) => {
    setDeleteTarget({ type: "single", id });
  }, []);

  const requestDeleteSelected = useCallback(() => {
    if (selected.size === 0) return;
    setDeleteTarget({ type: "bulk", ids: Array.from(selected) });
  }, [selected]);

  const closeDeleteModal = useCallback(() => {
    if (confirmingDelete) return;
    setDeleteTarget(null);
  }, [confirmingDelete]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    setConfirmingDelete(true);
    try {
      if (deleteTarget.type === "single") {
        const res = await cfFetch(`/transactions/${deleteTarget.id}`, {
          method: "DELETE",
        });
        if (res.ok) {
          setSelected((prev) => {
            if (!prev.has(deleteTarget.id)) return prev;
            const next = new Set(prev);
            next.delete(deleteTarget.id);
            return next;
          });
          mutate();
        }
      } else {
        await cfFetch("/transactions", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: deleteTarget.ids }),
        });
        setSelected(new Set());
        mutate();
      }
    } finally {
      setConfirmingDelete(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, mutate]);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-slate-500">
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-slate-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="p-12 text-center text-slate-500">
        <p className="font-medium">No transactions found</p>
        <p className="text-sm mt-1">Upload an Excel file to get started</p>
      </div>
    );
  }

  return (
    <div>
      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 border-b border-blue-100 bg-blue-50 px-4 py-2">
          <span className="text-sm font-medium text-blue-700">
            {selected.size} selected
          </span>
          <button
            onClick={requestDeleteSelected}
            disabled={confirmingDelete}
            className="flex items-center gap-1.5 px-3 py-1 text-sm text-white rounded-lg bg-slate-400 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5 text-white" />
            {confirmingDelete && deleteTarget?.type === "bulk"
              ? "Deleting…"
              : "Delete selected"}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-sm text-blue-500 hover:text-blue-700"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="max-h-[600px] overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#d8e0ea] bg-[#f8fafc]">
              <th className="px-4 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="rounded border-slate-300 accent-blue-600 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#74869e]">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#74869e]">
                Description
              </th>
              <th className="w-36 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-[#74869e]">
                Amount
              </th>
              <th className="w-36 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-[#74869e]">
                Balance
              </th>
              <th className="w-px whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#74869e]">
                Category
              </th>
              <th className="w-10 pr-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx: TransactionWithCategory) => {
              const uncategorized = isUncategorizedTransaction(tx);
              const isSelected = selected.has(tx.id);
              return (
                <tr
                  key={tx.id}
                  className={`border-b transition-colors ${
                    isSelected
                      ? "bg-blue-50"
                      : uncategorized
                        ? "border-amber-100 bg-amber-50/70 hover:bg-amber-50"
                        : "border-[#edf2f7] hover:bg-[#f8fafd]"
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(tx.id)}
                      onChange={() => toggleOne(tx.id)}
                      className="rounded border-slate-300 accent-blue-600 cursor-pointer"
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-[#647892]">
                    {formatAppDate(tx.date)}
                  </td>
                  <td
                    className="max-w-xs truncate px-4 py-3 text-[#1f2f45]"
                    title={tx.originalDescription}
                  >
                    {tx.originalDescription}
                  </td>
                  <td
                    className={`w-36 whitespace-nowrap px-4 py-3 text-right font-mono text-[13px] font-semibold ${tx.amount >= 0 ? "text-[#22a957]" : "text-[#e23f3f]"}`}
                  >
                    {formatSignedAmount(tx.amount, tx.currency)}
                  </td>
                  <td className="w-36 whitespace-nowrap px-4 py-3 text-right font-mono text-[13px] font-medium text-[#344257]">
                    {formatBalance(tx.balance, tx.currency)}
                  </td>
                  <td className="w-px whitespace-nowrap pl-4 pr-0 py-3">
                    <CategorySelect
                      transaction={tx}
                      onUpdate={handleUpdateCategory}
                    />
                  </td>
                  <td className="pl-2 pr-4 text-center">
                    <button
                      onClick={() => requestDeleteSingle(tx.id)}
                      className="cursor-pointer mx-auto inline-flex h-6 w-6 items-center justify-center text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-[#d8e0ea] px-4 py-3">
        <p className="text-sm text-[#6f8198]">
          {total} transactions total
          {someSelected && (
            <span className="ml-2 text-blue-600">
              · {selected.size} selected
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => updateFilter("page", (page ?? 1) - 1)}
            disabled={(page ?? 1) <= 1}
            className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-[#6f8198]">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => updateFilter("page", (page ?? 1) + 1)}
            disabled={(page ?? 1) >= totalPages}
            className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      <ConfirmationModal
        open={deleteTarget !== null}
        title={
          deleteTarget?.type === "bulk"
            ? `Delete ${deleteTarget.ids.length} transaction${deleteTarget.ids.length === 1 ? "" : "s"}?`
            : "Delete this transaction?"
        }
        description="This action cannot be undone."
        confirmLabel={
          deleteTarget?.type === "bulk" ? "Delete selected" : "Delete"
        }
        isLoading={confirmingDelete}
        onCancel={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
